import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import useIsMounted from '../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import minions from '../../../../minions/minions';
import colors from '../../../../constants/colors';

export default function PublicationInfo({ elementID, onClose }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const itLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID],
  );
  const itLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2?.[elementID],
  );
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const isShortVersion = useSelector(
    (s) => s.module[moduleBridgeHex]?.permissions?.resourceManager?.shortVersion,
  );
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );

  const handleItemViewLog = useCallback(
    async () =>
      axios.post(axiosPath('api_r_user_module_documentManager_item_view'), {
        moduleBridgeHex,
        itemHex: itLevel1?.hex,
      }),
    [moduleBridgeHex, itLevel1],
  );

  useEffect(() => {
    handleItemViewLog();
  }, []);

  const handleDownload = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_documentManager_item_publication_download'),
          {
            moduleBridgeHex,
            itemHex: itLevel1?.hex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(data, '_blank');
            onClose();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, itLevel1],
  );

  return (
    <div className="row">
      <div className="col-lg-6">
        <h6>Título de la publicación</h6>
        <div className="mb-3">{itLevel1?.title}</div>
        {!isShortVersion && (
          <>
            <h6>Resumen del contenido</h6>
            <div className="mb-3">{itLevel2?.description || '--'}</div>
            <h6>Autor o institución emisora de la publicación</h6>
            <div className="mb-3">{itLevel2?.issuer || '--'}</div>
          </>
        )}
        <h6>Año de publicación / creación / edición</h6>
        <div className="mb-3">{itLevel2?.publicationYear || '--'}</div>
        {!isShortVersion && (
          <>
            <h6>Diario o periódico oficial donde fue publicado</h6>
            <div className="mb-3">
              {moduleData?.inputs?.publicationLocations?.find(
                (i) => i?.value === itLevel2?.publicationLocation,
              )?.label || '--'}
            </div>
            <h6>Fecha de publicación</h6>
            <div className="mb-3">{itLevel2?.publicationDateStamp || '--'}</div>
            <h6>Fecha de aportación</h6>
            <div className="mb-3">{itLevel2?.dateStamp || '--'}</div>
            <h6>Tipo de publicación</h6>
            <div className="mb-3">
              {moduleData?.inputs?.publicationTypes?.find(
                (i) => i?.value === itLevel2?.publicationType,
              )?.label || '--'}
            </div>
            <h6>Recurso aportado por</h6>
            <div className="mb-3">
              {moduleData?.inputs?.userBridges?.find(
                (i) => i?.value === itLevel2?.userBridge,
              )?.label || '--'}
            </div>
          </>
        )}
        <h6>Archivo asociado</h6>
        {itLevel1?.fileName ? (
          <div className="mb-3">
            <File class onClick={handleDownload} title="Descargar">
              {minions.files.fileIcon(itLevel1?.fileName)} {itLevel1?.fileName}
            </File>
          </div>
        ) : (
          <div className="alert alert-info">No existe archivo asociado</div>
        )}
      </div>
      <div className="col-lg-6">
        {!isShortVersion && (
          <>
            <h5>Clasificación</h5>
            <h6>Temática principal</h6>
            <div className="mb-3">
              {moduleData?.inputs?.mainSubjects?.find(
                (i) => i?.value === itLevel2?.mainSubject,
              )?.label || '--'}
            </div>
            <h6>Temáticas secundarias</h6>
            <div className="mb-3">
              {itLevel2?.secondarySubjects?.length > 0 ? (
                <ul>
                  {itLevel2?.secondarySubjects?.map((ss) => (
                    <li key={ss}>
                      {moduleData?.inputs?.secondarySubjects?.find(
                        (i) => i?.value === ss,
                      )?.label || '--'}
                    </li>
                  ))}
                </ul>
              ) : (
                '--'
              )}
            </div>
            <h5>Ubicación geográfica</h5>
            <h6>Localizaciones</h6>
            <div className="mb-3">
              {itLevel2?.locations?.length > 0 ? (
                <ul>
                  {itLevel2?.locations?.map((l) => (
                    <li key={l?.value}>{l?.label}</li>
                  ))}
                </ul>
              ) : (
                '--'
              )}
            </div>
            <h6>Región natural</h6>
            <div className="mb-3">
              {itLevel2?.naturalRegions?.length > 0 ? (
                <ul>
                  {itLevel2?.naturalRegions?.map((nr) => (
                    <li key={nr?.id}>{nr?.label}</li>
                  ))}
                </ul>
              ) : (
                '--'
              )}
            </div>
          </>
        )}
        <h6>Palabras clave</h6>
        <div className="mb-3">
          {itLevel1?.tags?.length > 0
            ? itLevel1?.tags?.map((t) => (
                <span
                  key={t?.id}
                  className="badge badge-xs ml-1 pl-1 pr-1"
                  style={{
                    backgroundColor: colors.paletteColors[t?.color],
                    color: '#fff',
                  }}
                >
                  {t?.name}
                </span>
              ))
            : '--'}
        </div>
      </div>
    </div>
  );
}

const File = styled.span`
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;
